import { h, replaceChildren } from '@/components/dom';
import { button, checkbox, note, numberField, row, section } from '@/components/controls';
import { EXPORT_FORMATS } from '@/export/exporters';
import type { App } from './App';

export function createExportPanel(app: App): HTMLElement {
  let pngSize = 2048;
  let pngSoftware = false;
  const size = numberField({ label: 'PNG size (px)', value: pngSize, min: 64, max: 8192, step: 64, onChange: (v) => (pngSize = v) });
  const software = checkbox({ label: 'Render PNG with the engine (physical model)', value: pngSoftware, onChange: (v) => (pngSoftware = v) });
  const mm = numberField({ label: 'mm per target pixel', value: app.state.exportOptions.physical.mmPerPixel, min: 0.001, step: 0.1, onChange: (v) => app.store.set((s) => ({ exportOptions: { ...s.exportOptions, physical: { ...s.exportOptions.physical, mmPerPixel: Math.max(0.001, v) } } })) });
  const flip = checkbox({ label: 'Flip Y for machine coordinates', value: app.state.exportOptions.physical.flipY, onChange: (v) => app.store.set((s) => ({ exportOptions: { ...s.exportOptions, physical: { ...s.exportOptions.physical, flipY: v } } })) });
  const feed = numberField({ label: 'G-code feed (mm/min)', value: app.state.exportOptions.gcode.feedRate, min: 1, step: 100, onChange: (v) => app.store.set((s) => ({ exportOptions: { ...s.exportOptions, gcode: { ...s.exportOptions.gcode, feedRate: Math.max(1, v) } } })) });
  const svgPins = checkbox({ label: 'SVG: include pins', value: app.state.exportOptions.svg.includePins, onChange: (v) => app.store.set((s) => ({ exportOptions: { ...s.exportOptions, svg: { ...s.exportOptions.svg, includePins: v } } })) });
  const buttons = EXPORT_FORMATS.map((f) => button({ label: f.label, title: f.description, variant: f.id === 'png' || f.id === 'json' ? 'primary' : 'secondary', onClick: () => void app.export(f.id, pngSize, pngSoftware) }));
  const plan = h('div', { class: 'info-box' }, 'Physical plan: not computed.');
  const planBtn = button({
    label: 'Compute physical plan',
    onClick: async () => {
      try {
        const p = await app.client.physicalPlan(app.state.exportOptions.physical);
        replaceChildren(
          plan,
          h('table', { class: 'stat-table' },
            h('tr', null, h('td', null, 'Strings'), h('td', null, String(p.steps.length))),
            h('tr', null, h('td', null, 'Thread length'), h('td', null, `${(p.totalStringLengthMm / 1000).toFixed(2)} m`)),
            h('tr', null, h('td', null, 'Travel between threads'), h('td', null, `${(p.totalTravelMm / 1000).toFixed(2)} m`)),
            h('tr', null, h('td', null, 'Thread breaks'), h('td', null, String(p.threadBreaks))),
            h('tr', null, h('td', null, 'Continuous'), h('td', null, p.isContinuous ? 'yes' : 'no')),
            h('tr', null, h('td', null, 'Busiest pin'), h('td', null, `${Math.max(0, ...p.pinVisits)} strings`)),
          ),
        );
      } catch (err) {
        app.fail(err instanceof Error ? err.message : String(err));
      }
    },
  });
  app.store.subscribe((s, p) => {
    if (s.solution.pins !== p.solution.pins || s.status !== p.status || s === p) {
      const disabled = s.solution.pins.length === 0 || s.status === 'running';
      for (const b of buttons) b.disabled = disabled;
      planBtn.disabled = disabled;
    }
  }, { immediate: true });
  return h(
    'div',
    null,
    section('Export', [h('div', { class: 'export-grid' }, ...buttons), note('PNG and JSON use the current playhead; text formats export the full sequence.')]),
    section('Options', [size.element, software.element, svgPins.element, mm.element, flip.element, feed.element]),
    section('Physical build', [row(planBtn), plan, note('DXF and G-code use the millimetre scale above; the plan lists thread length and travel for machine planning.')]),
  );
}
